import React, { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Progress } from '@/components/ui/progress';
import { Shuffle, RotateCcw, Clock, Timer } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import GameCard from './GameCard';
import type { GameLevel, GameGrid, GameItem } from '@/data/gameData';

interface GameBoardProps {
  onGameComplete: (moves: number, timeInSeconds: number) => void;
  levelData: {
    level: GameLevel | undefined;
    grid: GameGrid | undefined;
  };
}

// Interface pour les cartes du jeu avec paires
interface GameCard {
  id: string;
  pairId: string; // ID unique pour identifier les paires
  item: GameItem;
}

const GameBoard = ({ onGameComplete, levelData }: GameBoardProps) => {
  const { t } = useTranslation();
  const { level, grid } = levelData;

  const [gameCards, setGameCards] = useState<GameCard[]>([]);
  const [selectedCards, setSelectedCards] = useState<string[]>([]);
  const [matchedPairs, setMatchedPairs] = useState<string[]>([]); // Store pair IDs that are matched
  const [moves, setMoves] = useState(0);
  const [timeRemaining, setTimeRemaining] = useState(0);
  const [gameStarted, setGameStarted] = useState(false);
  const [gameEnded, setGameEnded] = useState(false);

  // Créer des paires à partir des éléments de la grille pour une grille 4x4 (16 cartes = 8 paires)
  const createGameCards = (gridItems: GameItem[]): GameCard[] => {
    // Prendre les 8 premiers éléments pour créer 8 paires (16 cartes total)
    const selectedItems = gridItems.slice(0, 8);
    const cards: GameCard[] = [];
    
    selectedItems.forEach((item, index) => {
      const pairId = `pair_${index}`;
      // Créer deux cartes pour chaque élément (une paire)
      cards.push(
        { id: `${item.id}_1`, pairId, item },
        { id: `${item.id}_2`, pairId, item }
      );
    });
    
    // Mélanger les cartes pour randomiser l'ordre
    return cards.sort(() => Math.random() - 0.5);
  };

  useEffect(() => {
    if (grid?.items && level) {
      console.log('🎮 GameBoard - Initializing game with grid items:', grid.items.length);
      const cards = createGameCards(grid.items);
      console.log('🎮 GameBoard - Created cards:', cards.length, 'pairs:', cards.length / 2);
      setGameCards(cards);
      setGameStarted(true);
      setTimeRemaining(level.timeLimit);
      setMoves(0);
      setSelectedCards([]);
      setMatchedPairs([]);
      setGameEnded(false);
    }
  }, [grid, level]);

  // Timer countdown
  useEffect(() => {
    let timer: NodeJS.Timeout;
    if (gameStarted && !gameEnded && timeRemaining > 0 && matchedPairs.length < 8) {
      timer = setInterval(() => {
        setTimeRemaining(prev => {
          if (prev <= 1) {
            // Time's up!
            setGameEnded(true);
            const elapsedTime = level ? level.timeLimit : 0;
            onGameComplete(moves, elapsedTime);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    }
    return () => clearInterval(timer);
  }, [gameStarted, gameEnded, timeRemaining, matchedPairs.length, moves, onGameComplete, level]);

  const handleCardClick = (cardId: string) => {
    if (gameEnded || timeRemaining <= 0) return;
    
    const card = gameCards.find(c => c.id === cardId);
    if (!card || selectedCards.includes(cardId) || matchedPairs.includes(card.pairId)) {
      return;
    }

    const newSelectedCards = [...selectedCards, cardId];
    setSelectedCards(newSelectedCards);
    setMoves(prev => prev + 1);

    if (newSelectedCards.length === 2) {
      setTimeout(() => {
        const [firstCardId, secondCardId] = newSelectedCards;
        const firstCard = gameCards.find(c => c.id === firstCardId);
        const secondCard = gameCards.find(c => c.id === secondCardId);

        if (firstCard && secondCard && firstCard.pairId === secondCard.pairId) {
          // C'est une paire !
          console.log('🎮 GameBoard - Match found for pair:', firstCard.pairId);
          setMatchedPairs(prev => [...prev, firstCard.pairId]);
        }

        setSelectedCards([]);
      }, 1000);
    }
  };

  useEffect(() => {
    if (matchedPairs.length === 8 && gameCards.length > 0 && !gameEnded) { // 8 paires trouvées = victoire
      console.log('🎮 GameBoard - Game completed! All pairs found.');
      setGameEnded(true);
      const elapsedTime = level ? level.timeLimit - timeRemaining : 0;
      onGameComplete(moves, elapsedTime);
    }
  }, [matchedPairs.length, gameCards.length, gameEnded, moves, timeRemaining, onGameComplete, level]);

  const shuffleCards = () => {
    if (gameEnded || timeRemaining <= 0) return;
    const shuffled = [...gameCards].sort(() => Math.random() - 0.5);
    setGameCards(shuffled);
    setMoves(prev => prev + 1);
  };

  const formatTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const getTimeProgressPercentage = () => {
    if (!level) return 0;
    return ((level.timeLimit - timeRemaining) / level.timeLimit) * 100;
  };

  const getTimeProgressColor = () => {
    if (!level) return 'bg-green-500';
    const percentage = (timeRemaining / level.timeLimit) * 100;
    if (percentage > 50) return 'bg-green-500';
    if (percentage > 20) return 'bg-yellow-500';
    return 'bg-red-500';
  };

  const isTimeCritical = () => {
    if (!level) return false;
    return (timeRemaining / level.timeLimit) < 0.2;
  };

  if (!level || !grid) {
    return <div className="text-center">{t('game.loading')}</div>;
  }

  return (
    <div className="space-y-4">
      {/* Game Header */}
      <div className="text-center space-y-2">
        <h3 className="text-lg font-semibold text-gray-800">
          {t(`game.gridTitles.${grid.title}`, { defaultValue: grid.title })}
        </h3>
        <div className="flex justify-center gap-4 text-sm text-gray-600">
          <div className="flex items-center gap-1">
            <RotateCcw className="w-4 h-4" />
            <span>{moves} {t('game.moves')}</span>
          </div>
          <div className="flex items-center gap-1">
            <Timer className="w-4 h-4" />
            <span className={timeRemaining <= 10 ? 'text-red-600 font-bold' : ''}>
              {formatTime(timeRemaining)}
            </span>
          </div>
        </div>
      </div>

      {/* Time Progress Bar */}
      <Card className="bg-gradient-to-r from-blue-50 to-indigo-50">
        <CardContent className="p-3">
          <div className="flex justify-between items-center text-sm mb-2">
            <span className="text-gray-600 flex items-center gap-1">
              <Clock className="w-4 h-4" />
              Temps restant
            </span>
            <span className={`font-semibold ${timeRemaining <= 10 ? 'text-red-600' : 'text-blue-600'}`}>
              {formatTime(timeRemaining)}
            </span>
          </div>
          <div className="w-full bg-gray-200 rounded-full h-3">
            <div 
              className={`h-3 rounded-full transition-all duration-300 ${getTimeProgressColor()} ${
                isTimeCritical() ? 'animate-pulse' : ''
              }`}
              style={{ width: `${100 - getTimeProgressPercentage()}%` }}
            />
          </div>
        </CardContent>
      </Card>

      {/* Game Controls */}
      <div className="flex justify-center">
        <Button
          onClick={shuffleCards}
          variant="outline"
          size="sm"
          className="flex items-center gap-2"
          disabled={gameEnded || timeRemaining <= 0}
        >
          <Shuffle className="w-4 h-4" />
          Mélanger
        </Button>
      </div>

      {/* Game Grid - FORCER 4x4 avec 16 cartes */}
      <div className="grid grid-cols-4 gap-2 max-w-lg mx-auto">
        {gameCards.map((card) => {
          const isSelected = selectedCards.includes(card.id);
          const isMatched = matchedPairs.includes(card.pairId);
          
          return (
            <GameCard
              key={card.id}
              item={card.item}
              isSelected={isSelected}
              isMatched={isMatched}
              onClick={() => handleCardClick(card.id)}
            />
          );
        })}
      </div>

      {/* Progress - mise à jour pour 8 paires */}
      <Card className="bg-gradient-to-r from-purple-50 to-pink-50">
        <CardContent className="p-3">
          <div className="flex justify-between items-center text-sm">
            <span className="text-gray-600">Progression</span>
            <span className="font-semibold text-purple-600">
              {matchedPairs.length} / 8 paires
            </span>
          </div>
          <div className="w-full bg-gray-200 rounded-full h-2 mt-2">
            <div 
              className="bg-gradient-to-r from-purple-500 to-pink-500 h-2 rounded-full transition-all duration-300"
              style={{ width: `${(matchedPairs.length / 8) * 100}%` }}
            />
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default GameBoard;
